import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Label,
  Input,
  FormGroup,
} from 'reactstrap';
import Select from 'react-select';
import { ProductFormDetailsInterface } from 'types/productTypes';
import { unitPriceFields } from 'pages/Products/ProductConstants';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { GroupPrice } from 'types/MerchantTypes';
import { FormikProps } from 'formik';
import { RootState } from 'slices';
import { useParams } from 'react-router-dom';
import Cleave from 'cleave.js/react';
import { CustomReactSelectStyles } from 'pages/Products/styles/customStyles';
import styles from 'pages/Products/styles/products.module.css';
import { useEffect, useState } from 'react';
import { Option } from 'types/common/FormTypes';
import { setEditProductId, setEditStoreId } from 'slices/product/form/reducer';
import { Dispatch } from 'redux';

const ProductPriceInfo = ({
  isEdit,
  dispatch,
  formik,
}: {
  isEdit: boolean;
  dispatch: Dispatch;
  formik: FormikProps<ProductFormDetailsInterface>;
}) => {
  const { id } = useParams();
  const { t } = useTranslation();

  const layoutState = useSelector((state: RootState) => state.Layout);
  const productFormState = useSelector((state: RootState) => state.Product);

  const { layoutModeType } = layoutState;
  const {
    showAllValidateErrors,
    pageConfig,
    editStoreId,
    PriceBranchOptions,
    PriceBranch,
  } = productFormState.form;

  const isDarkMode = layoutModeType === 'dark';
  const [BranchValue, setBranchValue] = useState<Option | null>(null);

  useEffect(() => {
    if (!id || !PriceBranchOptions?.length) return;

    const found = PriceBranchOptions.find(
      (option) => String(option.value) === String(id)
    );

    if (found) {
      setBranchValue(found);
    }
  }, [id, PriceBranchOptions]);

  return (
    <Card>
      <CardHeader>
        <h5 className="card-title mb-0">{t('ProductPage_Prices')}</h5>
      </CardHeader>
      <CardBody className={`${styles['card-body-pd']}`}>
        <Row>
          <Col lg={6}>
            {isEdit && editStoreId && (
              <div className=" row">
                <Label htmlFor="#" className="col-sm-3 col-form-label">
                  {t('Branch')} <span style={{ color: 'red' }}>*</span>
                </Label>
                <Col sm={6}>
                  <FormGroup key={1}>
                    <Select
                      styles={CustomReactSelectStyles(isDarkMode)}
                      id="price_branch"
                      name="price_branch"
                      data-testid="price_branch"
                      options={PriceBranchOptions}
                      value={BranchValue}
                      onChange={(
                        option: { value: number; label: string } | null
                      ) => {
                        if (!option) return;

                        setBranchValue(option);
                        const rs = PriceBranch.find(
                          (price) => price.item_id === option.value
                        );
                        if (rs) {
                          dispatch(setEditProductId(rs.item_id));
                          dispatch(setEditStoreId(rs.store_id));
                          for (const [key, value] of Object.entries(
                            rs.prices[0]
                          )) {
                            formik
                              .setFieldValue(`${key}.value`, value)
                              .then(() => {
                                formik.setFieldTouched(`${key}.value`, true);
                              });
                          }
                        }
                      }}
                      placeholder={t('ProductPage_PleaseSelect')}
                    />
                  </FormGroup>
                </Col>
              </div>
            )}
            <div className="mb-3 row">
              <Label
                htmlFor="cost_price_value"
                className="col-sm-3 col-form-label"
              >
                {t('ProductField_CostPrice')}{' '}
                <span style={{ color: 'red' }}>*</span>
              </Label>

              <Col sm={6} xs={8}>
                <div>
                  <Cleave
                    className={`form-control`}
                    id="cost_price_value"
                    name="cost_price.value"
                    value={formik.values.cost_price.value}
                    options={{
                      numeral: true,
                      numeralDecimalScale: 2,
                      numeralIntegerScale: 13,
                      numeralThousandsGroupStyle: 'thousand',
                    }}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    required
                  />
                  {(formik.touched.cost_price?.value &&
                    formik.errors.cost_price?.value &&
                    typeof formik.errors.cost_price?.value === 'string') ||
                  (formik.isValid === false &&
                    formik.errors.cost_price &&
                    showAllValidateErrors === true) ? (
                    <div className="text-danger">
                      {formik.errors.cost_price.value}
                    </div>
                  ) : null}
                </div>
              </Col>
              <Col sm={3} xs={4}>
                <div
                  className={`form-check ${
                    isEdit ? '' : styles['check-box-hide']
                  }`}
                >
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="cost_price_apply_all"
                    name="cost_price.apply_all"
                    checked={formik.values.cost_price.apply_all === 1}
                    onChange={(e) => {
                      formik.setFieldValue('cost_price', {
                        ...formik.values.cost_price,
                        apply_all: e.target.checked ? 1 : 0,
                      });
                    }}
                  />
                  <Label
                    className="form-check-label"
                    htmlFor="cost_price_apply_all"
                  >
                    {t('ProductField_EditAllPermittedBranch')}
                  </Label>
                </div>
              </Col>
            </div>

            {unitPriceFields.map((unitPriceField) => {
              const priceObj: GroupPrice | undefined =
                pageConfig?.group_price?.find?.(
                  (item) => item.id === unitPriceField.id
                );
              const displayStyle = priceObj ? {} : { display: 'none' };

              return (
                <div
                  className="mb-3 row"
                  key={unitPriceField.id}
                  style={displayStyle}
                >
                  <Label
                    htmlFor={`${unitPriceField.fieldName}_value`}
                    className="col-sm-3 col-form-label"
                  >
                    {priceObj ? (
                      unitPriceField.id === 0 ? (
                        <>
                          {priceObj.name}{' '}
                          <span style={{ color: 'red' }}>*</span>
                        </>
                      ) : (
                        priceObj.name
                      )
                    ) : (
                      ''
                    )}
                  </Label>

                  <Col sm={6} xs={8}>
                    <div>
                      <Cleave
                        className={`form-control`}
                        id={`${unitPriceField.fieldName}_value`}
                        name={`${unitPriceField.fieldName}.value`}
                        value={
                          typeof formik.values[
                            unitPriceField.fieldName as keyof ProductFormDetailsInterface
                          ] === 'object' &&
                          formik.values[
                            unitPriceField.fieldName as keyof ProductFormDetailsInterface
                          ] !== null
                            ? (
                                formik.values[
                                  unitPriceField.fieldName as keyof ProductFormDetailsInterface
                                ] as any
                              ).value
                            : ''
                        }
                        options={{
                          numeral: true,
                          numeralDecimalScale: 2,
                          numeralThousandsGroupStyle: 'thousand',
                        }}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        required={unitPriceField.id === 0}
                      />
                      {((
                        formik.touched[
                          unitPriceField.fieldName as keyof ProductFormDetailsInterface
                        ] as any
                      )?.value &&
                        (
                          formik.errors[
                            unitPriceField.fieldName as keyof ProductFormDetailsInterface
                          ] as any
                        )?.value &&
                        (
                          typeof formik.errors[
                            unitPriceField.fieldName as keyof ProductFormDetailsInterface
                          ] as any
                        )?.value === 'string') ||
                      (formik.isValid === false &&
                        formik.errors[
                          unitPriceField.fieldName as keyof ProductFormDetailsInterface
                        ] &&
                        showAllValidateErrors === true) ? (
                        <div className="text-danger">
                          {
                            (
                              formik.errors[
                                unitPriceField.fieldName as keyof ProductFormDetailsInterface
                              ] as any
                            ).value
                          }
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col sm={3} xs={4}>
                    <div
                      className={`form-check ${
                        isEdit ? '' : styles['check-box-hide']
                      }`}
                    >
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id={`${unitPriceField.fieldName}_apply_all`}
                        name={`${unitPriceField.fieldName}.apply_all`}
                        checked={
                          typeof formik.values[
                            unitPriceField.fieldName as keyof ProductFormDetailsInterface
                          ] === 'object' &&
                          (
                            formik.values[
                              unitPriceField.fieldName as keyof ProductFormDetailsInterface
                            ] as any
                          ).apply_all === 1
                        }
                        onChange={(e) => {
                          const fieldValue =
                            formik.values[
                              unitPriceField.fieldName as keyof ProductFormDetailsInterface
                            ];
                          if (
                            typeof fieldValue === 'object' &&
                            fieldValue !== null
                          ) {
                            formik.setFieldValue(unitPriceField.fieldName, {
                              ...fieldValue,
                              apply_all: e.target.checked ? 1 : 0,
                            });
                          }
                        }}
                      />
                      <Label
                        className="form-check-label"
                        htmlFor={`${unitPriceField.fieldName}_apply_all`}
                      >
                        {t('ProductField_EditAllPermittedBranch')}
                      </Label>
                    </div>
                  </Col>
                </div>
              );
            })}

            {/* //? ======== TAX ======== */}
            {/* <div className="mb-3 row">
              <Label htmlFor="vat" className="col-sm-3 col-form-label">
                {t('ProductField_Vat')}
              </Label>
              <Col sm={6} xs={8}>
                <div>
                  <Input
                    type="text"
                    className="form-control"
                    id="vat"
                    name="vat"
                    value={
                      displayVatPercentage === ''
                        ? formatNumberWithCommasAnd2Decimals(
                            formik.values.vat
                          ) + ' %'
                        : displayVatPercentage
                    }
                    onChange={(e) => {
                      handleChangeDisplayVatPercentage(e, formik, dispatch);
                    }}
                    onKeyUp={(e) => {
                      handleOnKeyDisplayVatPercentage(e, formik, dispatch);
                    }}
                    onBlur={(e) => {
                      handleBlurDisplayVatPercentage(e, formik, dispatch);
                    }}
                    onFocus={() => {
                      handleFocusDisplayVatPercentage(formik, dispatch);
                    }}
                  />
                </div>
              </Col>
              <Col sm={3} xs={4}>
                <div></div>
              </Col>
            </div> */}
          </Col>

          <Col lg={6}></Col>
        </Row>
      </CardBody>
    </Card>
  );
};
export default ProductPriceInfo;
